import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import IdleWatcher from "@/components/IdleWatcher";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Apply from "@/pages/Apply";
import Home from "@/pages/Home";
import VoiceAssessment from "@/pages/VoiceAssessment";
import Screening from "@/pages/Screening";
import ReportView from "@/pages/ReportView";
import VoiceLab from "@/pages/VoiceLab";
import QuestionnaireRun from "@/pages/QuestionnaireRun";
import Experts from "@/pages/Experts";
import ExpertDetail from "@/pages/ExpertDetail";
import Therapies from "@/pages/Therapies";
import Me from "@/pages/Me";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSettings from "@/pages/AdminSettings";
import DatpAdmin from "@/pages/DatpAdmin";
import LiveGroupRoom from "@/pages/LiveGroupRoom";
import SpecialistDashboard from "@/pages/SpecialistDashboard";
import Session from "@/pages/Session";
import DigestUnsubscribe from "@/pages/DigestUnsubscribe";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

import "@/App.css";

function SpecialistOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (!["clinical_specialist", "adjunctive_specialist"].includes(user.role)) return <Navigate to="/home" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <Toaster richColors position="top-center" />
        <IdleWatcher />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/digest/unsubscribe" element={<DigestUnsubscribe />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            {/* Specialist portal */}
            <Route
              path="/specialist"
              element={
                <ProtectedRoute>
                  <SpecialistOnly>
                    <SpecialistDashboard />
                  </SpecialistOnly>
                </ProtectedRoute>
              }
            />

            {/* Session (available to both patient and specialist) */}
            <Route
              path="/session/:id"
              element={
                <ProtectedRoute>
                  <Session />
                </ProtectedRoute>
              }
            />

            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/home" element={<Home />} />
              <Route path="/screening" element={<Screening />} />
              <Route path="/assess" element={<VoiceAssessment />} />
              <Route path="/report/:id" element={<ReportView />} />
              <Route path="/voicelab" element={<VoiceLab />} />
              <Route path="/voicelab/:qid" element={<QuestionnaireRun />} />
              <Route path="/experts" element={<Experts />} />
              <Route path="/experts/:id" element={<ExpertDetail />} />
              <Route path="/therapies" element={<Therapies />} />
              <Route path="/me" element={<Me />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/datp"
                element={
                  <ProtectedRoute roles={["platform_admin", "content_manager"]}>
                    <DatpAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/live-group/:tid"
                element={
                  <ProtectedRoute>
                    <LiveGroupRoom />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
